// NCCA Calendar — interactive month grid built from class_session metaobjects.
// Reads sessions from inline JSON, renders month grid with format dots,
// expands a drawer on date click, adds to cart with date as line-item property.

(function () {
  'use strict';

  // Visible text comes from window.NCCA_T, which sections/ncca-calendar.liquid prints from the theme's locale files, so
  // the Spanish site reads in Spanish. Every call carries the English as its fallback. {name} marks a value filled in here.
  function tr(k, en) { return (window.NCCA_T && window.NCCA_T[k]) || en; }
  function tfill(s, v) { return String(s).replace(/\{(\w+)\}/g, function (m, k) { return Object.prototype.hasOwnProperty.call(v, k) ? String(v[k]) : m; }); }

  const MONTHS = tr('cal_months', 'January,February,March,April,May,June,July,August,September,October,November,December').split(',');

  function monthYear(m, y) { return tfill(tr('cal_fmt_month_year', '{month} {year}'), { month: MONTHS[m], year: y }); }
  function monthDay(m, d) { return tfill(tr('cal_fmt_month_day', '{month} {day}'), { month: MONTHS[m], day: d }); }
  function onlyLeft(n) { return tfill(n === 1 ? tr('cal_only_left_one', 'Only {n} seat left') : tr('cal_only_left_other', 'Only {n} seats left'), { n: n }); }
  function cityState(city) { return tfill(tr('cal_city_state', '{city}, North Carolina'), { city: city }); }
  function registerPrice(price) { return tfill(tr('cal_register_price', 'Register · ${price}'), { price: price }); }

  function parseDate(str) {
    // YYYY-MM-DD → local date (avoid timezone drift from new Date('YYYY-MM-DD') being UTC)
    const [y, m, d] = str.split('-').map(Number);
    return new Date(y, m - 1, d);
  }

  function fmtDate(d) {
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
  }

  // Format YYYY-MM-DD → MM/DD/YYYY for display
  function fmtDisplayDate(iso) {
    if (!iso || !/^\d{4}-\d{2}-\d{2}/.test(iso)) return iso || '';
    const [y, m, d] = iso.slice(0, 10).split('-');
    return `${m}/${d}/${y}`;
  }

  // Plain-language "what you actually get" line, derived from the product type
  // so every card on a CE day reads differently (full-day vs bundle vs single).
  function sessionDescriptor(s) {
    const h = (s.productHandle || '').toLowerCase();
    if (s.format === 'On-site') return tr('cal_desc_onsite', 'Full-day in-person exam prep · taught live, 8 AM–4 PM');
    if (h.indexOf('full-compliance') !== -1) return tr('cal_desc_fc', 'Full day · all 8 CE hours — the 2-hr mandatory class + 3 electives in one sitting');
    if (h === 'nc-2-hour-mandatory-class') return tr('cal_desc_mandatory', 'The required 2-hr NC Board mandatory class · 2 of your 8 annual CE hours');
    if (/-bundle$/.test(h)) return tr('cal_desc_bundle', 'CE elective bundle · 3 electives = 6 CE hours (mandatory not included)');
    return tr('cal_desc_single', 'Single CE elective · 2 hours — mix & match toward your 6 elective hours');
  }

  function init(root) {
    const dataEl = root.querySelector('[data-sessions]');
    let allSessions = [];
    try { allSessions = JSON.parse(dataEl.textContent.trim() || '[]'); } catch (e) { console.error('NCCA calendar: bad JSON', e); }

    const adminMode = root.dataset.adminMode === 'true';
    // When the page itself IS the class-details / sales-funnel page (e.g. a dedicated
    // event landing page), hide the redundant "Class details →" link in the modal.
    const hideDetailsLink = root.dataset.hideDetailsLink === 'true';
    const adminStore = root.dataset.adminStore || '';
    const adminBaseUrl = adminStore ? `https://admin.shopify.com/store/${adminStore}` : '';
    const passcode = root.dataset.passcode || '';
    const workerUrl = (root.dataset.workerUrl || '').replace(/\/$/, '');
    const workerSecret = root.dataset.workerSecret || '';
    const workerEnabled = adminMode && workerUrl && workerSecret;

    // Full session list past Shopify's 50-item Liquid cap.
    const publicSessionsUrl = (root.dataset.publicSessionsUrl || '').replace(/\/$/, '');
    const preFilterProduct = root.dataset.filterProduct || '';
    const preFilterFormat = root.dataset.filterFormatPre || '';
    const preFilterCity = root.dataset.filterCity || '';
    const preFilterHandles = (root.dataset.filterHandles || '').split(',').map(function (h) { return h.trim(); }).filter(Boolean);
    const preFilterHandleSet = preFilterHandles.length ? new Set(preFilterHandles) : null;

    // Mirror the Liquid pre-filter (product / collection / format / city) so the
    // fetched full set shows exactly what this calendar instance is scoped to.
    function applyPreFilter(list) {
      return (list || []).filter(function (s) {
        if (preFilterProduct && s.productHandle !== preFilterProduct) return false;
        if (preFilterHandleSet && !preFilterHandleSet.has(s.productHandle)) return false;
        if (preFilterFormat === 'online' && s.format === 'On-site') return false;
        if (preFilterFormat && preFilterFormat !== 'any' && preFilterFormat !== 'online' && s.format !== preFilterFormat) return false;
        if (preFilterCity && s.city !== preFilterCity && s.format === 'On-site') return false;
        return true;
      });
    }

    // Liquid's shop.metaobjects.*.values caps at 50 entries. Fetch the full list
    // from the Worker (public, cached) and swap it in; fall back to the embedded
    // JSON if the fetch fails so the calendar always works.
    if (publicSessionsUrl) {
      fetch(publicSessionsUrl)
        .then(function (r) { return r.ok ? r.json() : null; })
        .then(function (data) {
          if (data && data.ok && Array.isArray(data.sessions) && data.sessions.length) {
            allSessions = applyPreFilter(data.sessions);
            if (!userNavigated && typeof jumpToEarliest === 'function') jumpToEarliest();
            if (typeof render === 'function') render();
            if (typeof renderFeatured === 'function') renderFeatured();
            if (typeof updateRegisterWidgets === 'function') updateRegisterWidgets();
            if (typeof pruneFormatChips === 'function') pruneFormatChips();
          }
        })
        .catch(function () { /* keep embedded fallback */ });
    }

    // Products list — try Liquid first (fast), fall back to Worker fetch (reliable)
    let productsList = [];
    let productsLoading = false;
    let currentFormState = null; // { mode: 'edit' | 'create', session?, defaultDate? }

    if (adminMode) {
      const productsEl = root.querySelector('[data-products]');
      if (productsEl) {
        try { productsList = JSON.parse(productsEl.textContent.trim() || '[]'); }
        catch (e) { console.error('NCCA calendar: bad products JSON', e); }
      }
      // If Liquid returned empty (collections.all quirk in JSON templates),
      // fetch from Worker as authoritative source
      if (workerUrl && workerSecret && productsList.length === 0) {
        productsLoading = true;
        fetch(workerUrl + '/products/list', { headers: { 'X-NCCA-Auth': workerSecret } })
          .then(r => r.json())
          .then(data => {
            productsLoading = false;
            if (data.ok) {
              productsList = data.products;
              // If a form is open and waiting on products, re-render it
              if (currentFormState) refreshOpenForm();
            }
          })
          .catch(e => { productsLoading = false; console.error('Products fetch failed:', e); });
      }
    }

    // Attendee counts (admin mode only) — { "YYYY-MM-DD|product-handle": count }
    let attendeeCounts = {};
    if (adminMode && workerUrl && workerSecret) {
      fetch(workerUrl + '/sessions/attendees', { headers: { 'X-NCCA-Auth': workerSecret } })
        .then(r => r.json())
        .then(data => {
          if (data.ok) {
            attendeeCounts = data.counts;
            // Re-render so booked counts appear on the grid
            if (typeof render === 'function') render();
          }
        })
        .catch(e => console.error('Attendees fetch failed:', e));
    }
    function bookedFor(session) {
      const key = `${session.date}|${session.productHandle}`;
      return attendeeCounts[key] || 0;
    }

    function showSaveToast(text) {
      let toast = document.querySelector('.ncca-cal__toast');
      if (!toast) {
        toast = document.createElement('div');
        toast.className = 'ncca-cal__toast';
        document.body.appendChild(toast);
      }
      toast.textContent = text;
      toast.classList.add('ncca-cal__toast--show');
      setTimeout(() => toast.classList.remove('ncca-cal__toast--show'), 2000);
    }

    function refreshOpenForm() {
      if (!currentFormState) return;
      if (currentFormState.mode === 'create') {
        drawerBody.innerHTML = renderCreateForm(currentFormState.defaultDate);
      } else {
        drawerBody.innerHTML = renderEditForm(currentFormState.session);
      }
      wireFormEvents();
    }

    // Password gate (admin mode only) — calendar still initializes underneath
    // the hidden wrapper, so on unlock we just reveal the already-rendered grid.
    if (adminMode && passcode) {
      const gate = root.querySelector('[data-cal-gate]');
      const protectedArea = root.querySelector('[data-cal-protected]');
      const gateForm = root.querySelector('[data-cal-gate-form]');
      const gateError = root.querySelector('[data-cal-gate-error]');
      const sessionKey = `ncca_cal_unlocked_${adminStore}`;

      const unlock = () => {
        if (gate) gate.style.display = 'none';
        if (protectedArea) protectedArea.hidden = false;
      };

      if (sessionStorage.getItem(sessionKey) === passcode) {
        unlock();
      } else if (gateForm) {
        gateForm.addEventListener('submit', (e) => {
          e.preventDefault();
          const entered = gateForm.querySelector('[name="passcode"]').value;
          if (entered === passcode) {
            sessionStorage.setItem(sessionKey, passcode);
            unlock();
          } else {
            if (gateError) gateError.hidden = false;
            gateForm.querySelector('[name="passcode"]').value = '';
          }
        });
        // No early return — calendar setup continues underneath the hidden wrapper.
      }
    }

    const monthLabel = root.querySelector('[data-cal-month-label]');
    const daysContainer = root.querySelector('[data-cal-days]');
    const drawer = root.querySelector('[data-cal-drawer]');
    const drawerTitle = root.querySelector('[data-cal-drawer-title]');
    const drawerBody = root.querySelector('[data-cal-drawer-body]');
    const drawerClose = root.querySelector('[data-cal-drawer-close]');
    const prevBtn = root.querySelector('[data-cal-prev]');
    const nextBtn = root.querySelector('[data-cal-next]');
    const chips = root.querySelectorAll('[data-filter-format]');

    let currentFilter = 'any';
    let view = new Date(); // current month being rendered
    let userNavigated = false; // once the user clicks prev/next, stop auto-jumping

    // Drop format chips this calendar has no sessions for. The chips are rendered server-side
    // before the session list is fetched, so an exam-prep page was offering a "Live Zoom"
    // filter that matched nothing (exam prep is in-person only). Also hides the legend entry.
    function pruneFormatChips() {
      if (!allSessions.length) return;
      const present = {};
      allSessions.forEach(function (s) { if (s && s.format) present[String(s.format)] = true; });
      let hidden = 0, visible = 0;
      chips.forEach(function (c) {
        const f = c.getAttribute('data-filter-format');
        if (!f || f === 'any') return;
        if (present[f]) { visible++; return; }
        c.hidden = true;
        hidden++;
        if (currentFilter === f) { currentFilter = 'any'; }
      });
      // One remaining format makes "All Formats" meaningless — hide the whole row.
      if (visible <= 1) {
        const bar = root.querySelector('.ncca-cal__filters');
        if (bar) bar.hidden = true;
      }
      if (hidden) {
        root.querySelectorAll('.ncca-cal__legend-item').forEach(function (li) {
          const dot = li.querySelector('.ncca-cal__dot');
          if (!dot) return;
          const isZoom = dot.classList.contains('ncca-cal__dot--zoom');
          const isOnsite = dot.classList.contains('ncca-cal__dot--onsite');
          if ((isZoom && !present['Zoom']) || (isOnsite && !present['On-site'])) li.hidden = true;
        });
      }
    }

    // Jump the view to the month of the earliest UPCOMING session in the current
    // (possibly-filtered) set. Re-run after the Worker fetch swaps in the full set,
    // so a filtered calendar lands on the first month that actually has a class
    // (not whatever happened to be in the 50-row embedded fallback).
    function jumpToEarliest() {
      if (!allSessions.length) return;
      const now = new Date();
      const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      const future = allSessions
        .map(function (s) { return parseDate(s.date); })
        .filter(function (d) { return d && d >= todayStart; })
        .sort(function (a, b) { return a - b; });
      if (future.length) view = new Date(future[0].getFullYear(), future[0].getMonth(), 1);
    }
    jumpToEarliest();

    // Registration closes at 4:00 PM the day BEFORE the class date.
    function isOpenForRegistration(iso) {
      if (!iso || !/^\d{4}-\d{2}-\d{2}/.test(iso)) return true;
      const d = parseDate(iso.slice(0, 10));
      const cutoff = new Date(d.getFullYear(), d.getMonth(), d.getDate() - 1, 16, 0, 0, 0);
      return new Date() < cutoff;
    }

    function filtered() {
      let list = currentFilter === 'any' ? allSessions : allSessions.filter(s => s.format === currentFilter);
      // Public calendar hides classes past their registration cutoff. Admin/staff
      // calendar shows everything so past sessions can still be edited/deleted.
      if (!adminMode) list = list.filter(s => isOpenForRegistration(s.date));
      return list;
    }

    function sessionsByDate() {
      const map = {};
      filtered().forEach(s => {
        if (!map[s.date]) map[s.date] = [];
        map[s.date].push(s);
      });
      return map;
    }

    function render() {
      const year = view.getFullYear();
      const month = view.getMonth();
      monthLabel.textContent = monthYear(month, year);

      const firstDay = new Date(year, month, 1);
      const startWeekday = firstDay.getDay(); // 0 Sun
      const daysInMonth = new Date(year, month + 1, 0).getDate();
      const byDate = sessionsByDate();

      let html = '';
      // Leading blanks
      for (let i = 0; i < startWeekday; i++) {
        html += '<div class="ncca-cal__day ncca-cal__day--blank" aria-hidden="true"></div>';
      }
      // Days
      const today = fmtDate(new Date());
      for (let d = 1; d <= daysInMonth; d++) {
        const iso = `${year}-${String(month+1).padStart(2,'0')}-${String(d).padStart(2,'0')}`;
        const sessions = byDate[iso] || [];
        const hasSessions = sessions.length > 0;
        const isToday = iso === today;
        const formats = [...new Set(sessions.map(s => s.format))];
        const dots = formats.map(f => {
          const cls = f === 'Zoom' ? 'zoom' : f === 'On-site' ? 'onsite' : 'elearning';
          return `<span class="ncca-cal__dot ncca-cal__dot--${cls}" aria-hidden="true"></span>`;
        }).join('');

        const cellClickable = hasSessions || adminMode;

        // Admin: show total booked across all sessions on this date
        let bookedBadge = '';
        if (adminMode && hasSessions) {
          const totalBooked = sessions.reduce((sum, s) => sum + bookedFor(s), 0);
          if (totalBooked > 0) {
            bookedBadge = `<span class="ncca-cal__booked" title="${totalBooked} attendee${totalBooked > 1 ? 's' : ''} booked">${totalBooked} 👤</span>`;
          }
        }

        html += `
          <button type="button"
                  class="ncca-cal__day${hasSessions ? ' ncca-cal__day--has' : ''}${isToday ? ' ncca-cal__day--today' : ''}${adminMode && !hasSessions ? ' ncca-cal__day--add' : ''}"
                  ${cellClickable ? `data-cal-open="${iso}"` : 'disabled'}
                  aria-label="${hasSessions ? tfill(sessions.length > 1 ? tr('cal_aria_sessions_other', '{n} sessions on {date}') : tr('cal_aria_sessions_one', '{n} session on {date}'), { n: sessions.length, date: monthDay(month, d) }) : (adminMode ? `${MONTHS[month]} ${d} — add new session` : tfill(tr('cal_aria_no_sessions', '{date} — no sessions'), { date: monthDay(month, d) }))}">
            <span class="ncca-cal__daynum">${d}</span>
            ${hasSessions ? `<span class="ncca-cal__dots">${dots}</span><span class="ncca-cal__count">${sessions.length}</span>${bookedBadge}` : (adminMode ? '<span class="ncca-cal__add-icon">+</span>' : '')}
          </button>`;
      }
      daysContainer.innerHTML = html;
      renderMonthCards();
    }

    // Featured "Upcoming In-Person Events" strip (Live & In-Person Classes page).
    // Renders a card per upcoming on-site session ABOVE the calendar; clicking one
    // jumps the grid to that date and opens its registration modal. Completely inert
    // on every other calendar instance — only runs when [data-cal-featured] exists.
    const calSection = root.closest('.ncca-cal');
    const featuredEl = calSection ? calSection.querySelector('[data-cal-featured]') : null;
    const featuredTrack = featuredEl ? featuredEl.querySelector('[data-cal-featured-track]') : null;
    const monthCardsEl = root.querySelector('[data-cal-monthcards]');
    const showMonthCards = root.dataset.showMonthCards !== 'false'; // default on for public calendars
    const WKDAYS = tr('cal_wkdays', 'Sun,Mon,Tue,Wed,Thu,Fri,Sat').split(',');
    const PIN_SVG = '<svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z"/><circle cx="12" cy="10" r="3"/></svg>';

    function renderFeatured() {
      if (!featuredEl || !featuredTrack || adminMode) return;
      const now = new Date();
      const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      const items = allSessions
        .filter(function (s) {
          if (s.format !== 'On-site') return false;
          const d = parseDate(s.date);
          return d && d >= todayStart && isOpenForRegistration(s.date);
        })
        .sort(function (a, b) { return parseDate(a.date) - parseDate(b.date); })
        .slice(0, 12);
      if (!items.length) { featuredEl.hidden = true; return; }
      featuredTrack.innerHTML = items.map(function (s) {
        const d = parseDate(s.date);
        const dateLabel = tfill(tr('cal_fmt_card_date', '{dow}, {mon} {day}'), { dow: WKDAYS[d.getDay()], mon: MONTHS[d.getMonth()].slice(0, 3), day: d.getDate() });
        const timeLabel = s.start ? (s.start + (s.end && s.end !== s.start ? '–' + s.end : '')) : '';
        const booked = (typeof s.booked === 'number') ? s.booked : bookedFor(s);
        const remaining = Math.max(0, (s.seatsRemaining || 0) - booked);
        const soldOut = remaining === 0;
        let seatTag;
        if (soldOut) seatTag = '<span class="ncca-cal__fcard-seats ncca-cal__fcard-seats--out">' + tr('cal_sold_out', 'Sold Out') + '</span>';
        else if (remaining < 8) seatTag = '<span class="ncca-cal__fcard-seats ncca-cal__fcard-seats--low">' + onlyLeft(remaining) + '</span>';
        else seatTag = '<span class="ncca-cal__fcard-seats">' + tfill(tr('cal_seats_left', '{n} seats left'), { n: remaining }) + '</span>';
        return '<button type="button" class="ncca-cal__fcard' + (soldOut ? ' ncca-cal__fcard--out' : '') + '" data-featured-open="' + s.date + '">'
          + '<span class="ncca-cal__fcard-head">'
            + '<span class="ncca-pill ncca-pill--on-site">' + tr('cal_in_person', 'In Person') + '</span>'
            + seatTag
          + '</span>'
          + '<span class="ncca-cal__fcard-date">' + dateLabel + (timeLabel ? ' · ' + timeLabel : '') + '</span>'
          + '<span class="ncca-cal__fcard-title">' + escHtml(s.productTitle) + '</span>'
          + (s.city ? '<span class="ncca-cal__fcard-city">' + PIN_SVG + cityState(escHtml(s.city)) + '</span>' : '')
          + '<span class="ncca-cal__fcard-desc">' + escHtml(sessionDescriptor(s)) + '</span>'
          + '<span class="ncca-cal__fcard-cta">' + (soldOut ? tr('cal_view_details', 'View details') : registerPrice(s.price)) + ' →</span>'
        + '</button>';
      }).join('');
      featuredEl.hidden = false;
    }

    // Readable card list of the SELECTED month's classes — stays in sync with the
    // grid month + the format filter (re-runs from render()). Built for visitors who
    // find the small grid hard to read. Clicking a card opens that day's registration
    // modal, exactly like clicking the grid date. Inert in admin mode or when disabled.
    // '8:00 AM' vs '10:00 AM' compared as STRINGS sorts 10, 12, 2, 8 — which put the 8 AM
    // mandatory class last. Parse to minutes past midnight and compare as numbers.
    function minutesOf(t) {
      const m = String(t || '').trim().match(/^(\d{1,2}):(\d{2})\s*([AaPp])/);
      if (!m) return 1e9;
      let h = parseInt(m[1], 10) % 12;
      if (m[3].toLowerCase() === 'p') h += 12;
      return h * 60 + parseInt(m[2], 10);
    }
    function renderMonthCards() {
      if (!monthCardsEl || adminMode || !showMonthCards) return;
      const year = view.getFullYear();
      const month = view.getMonth();
      const monthSessions = filtered()
        .filter(function (s) { const d = parseDate(s.date); return d && d.getFullYear() === year && d.getMonth() === month; })
        .sort(function (a, b) { return (parseDate(a.date) - parseDate(b.date)) || (minutesOf(a.start) - minutesOf(b.start)); });

      if (!monthSessions.length) {
        monthCardsEl.innerHTML = '<p class="ncca-cal__mc-empty">' + tfill(tr('cal_mc_empty', 'No classes scheduled in {monthyear}. Use the ‹ › arrows above to see other months.'), { monthyear: monthYear(month, year) }) + '</p>';
        return;
      }

      const lead = '<p class="ncca-cal__mc-lead">' + tfill(monthSessions.length === 1 ? tr('cal_mc_lead_one', '{n} class in {monthyear} — tap any card to register') : tr('cal_mc_lead_other', '{n} classes in {monthyear} — tap any card to register'), { n: monthSessions.length, monthyear: monthYear(month, year) }) + '</p>';

      monthCardsEl.innerHTML = lead + monthSessions.map(function (s) {
        const d = parseDate(s.date);
        const fmtClass = s.format === 'Zoom' ? 'zoom' : s.format === 'On-site' ? 'on-site' : 'elearning';
        const fmtLabel = s.format === 'On-site' ? tr('cal_in_person', 'In Person') : s.format;
        const booked = (typeof s.booked === 'number') ? s.booked : bookedFor(s);
        const remaining = Math.max(0, (s.seatsRemaining || 0) - booked);
        const soldOut = remaining === 0;
        let seatTag;
        if (soldOut) seatTag = '<span class="ncca-cal__mc-seats ncca-cal__mc-seats--out">' + tr('cal_sold_out', 'Sold Out') + '</span>';
        else if (remaining < 8) seatTag = '<span class="ncca-cal__mc-seats ncca-cal__mc-seats--low">' + onlyLeft(remaining) + '</span>';
        else seatTag = '<span class="ncca-cal__mc-seats">' + tfill(tr('cal_seats_left', '{n} seats left'), { n: remaining }) + '</span>';
        const timeLabel = s.start ? (s.start + (s.end && s.end !== s.start ? '–' + s.end : '')) : '';
        return '<button type="button" class="ncca-cal__mc-card' + (soldOut ? ' ncca-cal__mc-card--out' : '') + '" data-mc-open="' + s.date + '">'
          + '<span class="ncca-cal__mc-date">'
            + '<span class="ncca-cal__mc-date-dow">' + WKDAYS[d.getDay()] + '</span>'
            + '<span class="ncca-cal__mc-date-day">' + d.getDate() + '</span>'
            + '<span class="ncca-cal__mc-date-mon">' + MONTHS[d.getMonth()].slice(0, 3) + '</span>'
          + '</span>'
          + '<span class="ncca-cal__mc-body">'
            + '<span class="ncca-cal__mc-pills"><span class="ncca-pill ncca-pill--' + fmtClass + '">' + fmtLabel + '</span>' + seatTag + '</span>'
            + '<span class="ncca-cal__mc-title">' + escHtml(s.productTitle) + '</span>'
            + (s.format === 'On-site' && s.city ? '<span class="ncca-cal__mc-city">' + PIN_SVG + cityState(escHtml(s.city)) + '</span>' : '')
            + (timeLabel ? '<span class="ncca-cal__mc-time">' + escHtml(timeLabel) + '</span>' : '')
            + '<span class="ncca-cal__mc-desc">' + escHtml(sessionDescriptor(s)) + '</span>'
          + '</span>'
          + '<span class="ncca-cal__mc-cta">' + (soldOut ? tr('cal_view_details', 'View details') : registerPrice(s.price)) + ' <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M5 12h14"/><path d="m12 5 7 7-7 7"/></svg></span>'
        + '</button>';
      }).join('');
    }

    // External register widgets (e.g. a hero card or Shop Pay button on a landing
    // page). Any [data-ncca-register] opens THIS calendar's registration modal for
    // the earliest upcoming on-site session; any [data-ncca-live-seats] shows its
    // live seats-remaining. Inert unless such elements exist on the page.
    function getRegisterTarget() {
      const now = new Date();
      const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      const list = allSessions
        .filter(function (s) {
          if (s.format !== 'On-site') return false;
          const d = parseDate(s.date);
          return d && d >= todayStart && isOpenForRegistration(s.date);
        })
        .sort(function (a, b) { return parseDate(a.date) - parseDate(b.date); });
      return list[0] || null;
    }
    function updateRegisterWidgets() {
      if (adminMode) return;
      updateDayWidgets();
      const seatsEls = document.querySelectorAll('[data-ncca-live-seats]');
      if (!seatsEls.length) return;
      const t = getRegisterTarget();
      if (!t) return;
      const booked = (typeof t.booked === 'number') ? t.booked : bookedFor(t);
      const remaining = Math.max(0, (t.seatsRemaining || 0) - booked);
      let text, cls = '';
      if (remaining === 0) { text = tr('cal_sold_out_unavailable', 'Sold Out — no longer available'); cls = 'out'; }
      else if (remaining < 8) { text = onlyLeft(remaining); cls = 'low'; }
      else { text = tfill(tr('cal_seats_left', '{n} seats left'), { n: remaining }); }
      seatsEls.forEach(function (el) {
        el.textContent = text;
        el.classList.remove('ncca-live-seats--low', 'ncca-live-seats--out');
        if (cls) el.classList.add('ncca-live-seats--' + cls);
      });
    }

    /* ------------------------------------------------------------------
       MULTI-DAY LANDING PAGES (the ncca-event-days section)

       A three-day event has one card per day, so the [data-ncca-register]
       hook above cannot serve it: getRegisterTarget() always resolves to the
       EARLIEST upcoming on-site session, which would make all three buttons
       open Day 1. These two hooks address a day explicitly by its ISO date:

         [data-ncca-open-day="YYYY-MM-DD"]   click -> opens that day's modal
         [data-ncca-day-seats="YYYY-MM-DD"]        -> that day's seats left

       Both are inert unless such elements exist on the page, and the buttons
       keep their href="#calendar", so if the click is not intercepted the
       visitor still lands on the calendar. A script failure degrades to a
       scroll, never to a dead button.
       ------------------------------------------------------------------ */
    let dayClicksWired = false;
    function updateDayWidgets() {
      document.querySelectorAll('[data-ncca-day-seats]').forEach(function (el) {
        const iso = el.getAttribute('data-ncca-day-seats');
        if (!iso) return;
        const s = allSessions.filter(function (x) { return x.date === iso; })[0];
        if (!s) return;
        const booked = (typeof s.booked === 'number') ? s.booked : bookedFor(s);
        const remaining = Math.max(0, (s.seatsRemaining || 0) - booked);
        let text, cls = '';
        if (!isOpenForRegistration(iso)) { text = tr('cal_reg_closed', 'Registration closed'); cls = 'out'; }
        else if (remaining === 0) { text = tr('cal_sold_out_lc', 'Sold out'); cls = 'out'; }
        else if (remaining < 8) { text = onlyLeft(remaining); cls = 'low'; }
        else { text = tfill(tr('cal_seats_of', '{n} of {total} seats left'), { n: remaining, total: (s.seatsTotal || remaining) }); }
        el.textContent = text;
        el.classList.remove('ncca-live-seats--low', 'ncca-live-seats--out');
        if (cls) el.classList.add('ncca-live-seats--' + cls);
      });

      if (dayClicksWired) return;
      if (!document.querySelector('[data-ncca-open-day]')) return;
      dayClicksWired = true;
      document.addEventListener('click', function (e) {
        const a = e.target && e.target.closest ? e.target.closest('[data-ncca-open-day]') : null;
        if (!a) return;
        const iso = a.getAttribute('data-ncca-open-day');
        if (!iso) return;
        // Not loaded yet, or this day is outside the section's filters: let the
        // href="#calendar" fallback run rather than swallowing the click.
        if (!allSessions.some(function (x) { return x.date === iso; })) return;
        e.preventDefault();
        openDay(iso);
      });
    }

    function openDay(iso) {
      const sessions = filtered().filter(s => s.date === iso);
      const d = parseDate(iso);
      const dateStr = tfill(tr('cal_fmt_long_date', '{month} {day}, {year}'), { month: MONTHS[d.getMonth()], day: d.getDate(), year: d.getFullYear() });

      // Admin mode + empty day: show inline create form (or fallback deep-link)
      if (adminMode && !sessions.length) {
        drawerTitle.textContent = `${dateStr} · Add new session`;
        drawerBody.innerHTML = workerEnabled
          ? renderCreateForm(iso)
          : `<article class="ncca-cal__session ncca-cal__session--add">
              <h4 class="ncca-cal__session-title">Add a new class session for ${dateStr}</h4>
              <p class="ncca-cal__session-meta">Worker URL not configured. Open Shopify admin to add a session manually.</p>
              <a href="${adminBaseUrl}/content/entries/class_session/new" target="_blank" rel="noopener" class="ncca-btn ncca-btn--gold ncca-btn--sm">+ Add session in admin</a>
            </article>`;
        wireFormEvents();
        openModal();
        return;
      }

      if (!sessions.length) return;
      // dateStr is built from MONTHS array — already long-form. Add MM/DD/YYYY for clarity.
      drawerTitle.textContent = tfill(sessions.length > 1 ? tr('cal_drawer_title_other', '{date} ({mdy}) · {n} sessions') : tr('cal_drawer_title_one', '{date} ({mdy}) · {n} session'), { date: dateStr, mdy: fmtDisplayDate(iso), n: sessions.length });

      drawerBody.innerHTML = '<div class="ncca-cal__session-stack">' + sessions.map(s => {
        const formatClass = s.format === 'Zoom' ? 'zoom' : s.format === 'On-site' ? 'on-site' : 'elearning';
        // Online seats taken (paid orders, from the Worker feed; admin fetch as fallback).
        // seats_remaining already accounts for offline/manual signups, so the true
        // count = seats_remaining − online orders. Decrements as people buy on the site.
        const booked = (typeof s.booked === 'number') ? s.booked : bookedFor(s);
        const remaining = Math.max(0, (s.seatsRemaining || 0) - booked);
        const soldOut = remaining === 0;
        const seatsBadge = adminMode
          ? `<strong>${booked}</strong> booked online · ${remaining} of ${s.seatsTotal} seats left`
          : (soldOut ? tr('cal_sold_out', 'Sold Out') : tfill(remaining === 1 ? tr('cal_remaining_one', '{n} seat remaining') : tr('cal_remaining_other', '{n} seats remaining'), { n: remaining }));
        const seatsClass = soldOut ? 'ncca-cal__seats--out' : (remaining < 5 ? 'ncca-cal__seats--low' : '');
        const seats = seatsBadge;
        const cityLine = s.city ? ` · ${s.city}` : '';
        const isOnsite = s.format === 'On-site';
        const locBlock = isOnsite
          ? `<div class="ncca-cal__loc">
               ${s.city ? `<p class="ncca-cal__loc-city"><svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z"/><circle cx="12" cy="10" r="3"/></svg>${cityState(s.city)}</p>` : ''}
               <p class="ncca-cal__loc-venue"><span class="ncca-cal__loc-label">${tr('cal_venue', 'Venue')}</span><span class="ncca-cal__loc-val">${s.venueName || tr('cal_tbd', 'To Be Determined')}${s.venueRoom ? ` · ${s.venueRoom}` : ''}</span></p>
               ${s.venueAddress ? `<p class="ncca-cal__loc-addr">${s.venueAddress.replace(/\n/g, ', ')} · <a href="https://maps.google.com/?q=${encodeURIComponent(s.venueAddress)}" target="_blank" rel="noopener" class="ncca-cal__maps-link">${tr('cal_directions', 'Get directions')}</a></p>` : ''}
             </div>`
          : '';
        const adminControls = adminMode
          ? `<div class="ncca-cal__admin-controls">
               ${workerEnabled
                 ? `<button type="button" class="ncca-cal__admin-edit" data-edit-session='${escAttr(JSON.stringify(s))}'>✎ Edit here</button>
                    <button type="button" class="ncca-cal__admin-delete" data-delete-session="${s.id || s.numericId}" data-delete-title="${escAttr(s.productTitle)}">🗑 Delete</button>`
                 : `<a href="${adminBaseUrl}/content/entries/class_session/${s.numericId}" target="_blank" rel="noopener" class="ncca-cal__admin-edit">✎ Edit in admin</a>`
               }
             </div>`
          : '';
        return `
          <article class="ncca-cal__session">
            <div class="ncca-cal__session-pills">
              <span class="ncca-pill ncca-pill--${formatClass}">${s.format === 'On-site' ? tr('cal_in_person', 'In Person') : s.format}</span>
              <span class="ncca-pill ncca-pill--ghost">${s.start}${s.end && s.start !== s.end ? '–' + s.end : ''}</span>
              ${soldOut ? `<span class="ncca-pill ncca-pill--soldout">${tr('cal_pill_sold_out', 'Sold Out · No Longer Available')}</span>` : ''}
            </div>
            <h4 class="ncca-cal__session-title">${s.productTitle}</h4>
            <p class="ncca-cal__session-desc">${sessionDescriptor(s)}</p>
            ${locBlock}
            <p class="ncca-cal__session-meta">
              ${s.instructor ? `${tr('cal_instructor', 'Instructor:')} <strong>${s.instructor}</strong>` : ''}${s.instructor ? ' · ' : ''}<span class="ncca-cal__seats ${seatsClass}">${seats}</span>
            </p>
            ${s.regDeadline ? `<p class="ncca-cal__session-deadline">⏰ ${tr('cal_reg_closes', 'Registration closes')} <strong>${tfill(tr('cal_reg_closes_when', '4:00 PM ET, {date}'), { date: fmtDisplayDate(s.regDeadline) })}</strong></p>` : ''}
            ${adminControls}
            ${adminMode ? '' : `
            <form action="/cart/add" method="post" class="ncca-cal__session-form" data-register-form>
              <input type="hidden" name="id" value="${s.variantId}">
              <input type="hidden" name="quantity" value="1">
              <input type="hidden" name="properties[Class Date]" value="${iso}${cityLine}">
              <input type="hidden" name="properties[Format]" value="${s.format === 'On-site' ? 'In Person' : s.format}">
              ${s.venueName ? `<input type="hidden" name="properties[Venue]" value="${s.venueName}${s.venueRoom ? ' · ' + s.venueRoom : ''}">` : ''}
              ${s.instructor ? `<input type="hidden" name="properties[Instructor]" value="${s.instructor}">` : ''}
              <input type="hidden" name="properties[Time]" value="${s.start}${s.end && s.start !== s.end ? '–' + s.end : ''}">
              <label class="ncca-cal__qualifier">
                <span class="ncca-cal__qualifier-label">${tr('cal_phone_label', 'Phone number')} <em>${tr('cal_required', 'required')}</em></span>
                <input type="tel" name="properties[Phone]" class="ncca-cal__qualifier-input" required minlength="7" autocomplete="tel" inputmode="tel" placeholder="(910) 555-1234">
              </label>
              ${(!/nascla|exam[- ]?prep|boot[- ]?camp/i.test((s.productHandle || '') + ' ' + (s.productTitle || ''))) ? `
              <label class="ncca-cal__qualifier">
                <span class="ncca-cal__qualifier-label">${tr('cal_qualifier_label', 'NCLBGC Qualifier ID')} <em>${tr('cal_required', 'required')}</em></span>
                <input type="text" name="properties[NCLBGC Qualifier ID]" class="ncca-cal__qualifier-input" required minlength="5" autocomplete="off" placeholder="${tr('cal_qualifier_ph', 'Enter your qualifier number')}">
              </label>
              ` : ''}
              <button type="submit" class="ncca-btn ncca-btn--gold ncca-btn--sm" ${soldOut ? 'disabled' : ''}>
                <span class="ncca-cal__btn-label">${soldOut ? tr('cal_btn_sold_out', 'Sold Out — No Longer Available') : registerPrice(s.price)}</span>
                <span class="ncca-cal__btn-spinner" aria-hidden="true"></span>
              </button>
              ${soldOut ? '' : spiLine(s.price)}
              ${hideDetailsLink ? '' : `<a href="${s.productUrl}" class="ncca-cal__session-link">${tr('cal_class_details', 'Class details →')}</a>`}
            </form>
            `}
          </article>
        `;
      }).join('') + '</div>'
        + (adminMode && workerEnabled ? `<button type="button" class="ncca-btn ncca-btn--gold ncca-btn--sm ncca-cal__add-another" data-add-session="${iso}">+ Add Another Event</button>` : '');
      if (drawerBody.querySelector('.ncca-spi-line')) drawerBody.insertAdjacentHTML('beforeend', spiLegal());

      wireFormEvents();
      openModal();
    }

    // ============================================================
    // INLINE EDIT/CREATE FORMS (admin mode + worker enabled)
    // ============================================================

    function escAttr(s) { return String(s).replace(/&/g, '&amp;').replace(/'/g, '&#39;').replace(/"/g, '&quot;'); }
    // Shop Pay Installments line under each Register button (Adam, 3 Oct 2026). Only $50 to $999.99, the band where this
    // store's Shop Pay plans include 4 interest-free installments; Shopify requires the disclosure (spiLegal) with it, and
    // the extended-delivery sentence because a class date can be weeks after the purchase.
    function spiLine(price) {
      const p = parseFloat(String(price == null ? '' : price).replace(/[^0-9.]/g, ''));
      if (!(p >= 50 && p < 1000)) return '';
      const part = (Math.round(p * 100 / 4) / 100).toFixed(2).replace(/\.00$/, '');
      return `<p class="ncca-spi-line ncca-cal__spi">${tfill(tr('spi_line', 'or 4 interest-free installments of {amount} with'), { amount: '$' + part })} <b>Shop Pay</b></p>`;
    }
    function spiLegal() {
      return '<p class="ncca-spi-legal ncca-cal__spi-legal">' + tr('spi_delivery_note', 'If you finance your purchase with Affirm, your first payment is due approximately two weeks after your purchase is finalized. Please note that due to extended delivery times, your loan payment(s) may be due before you receive your purchase.') + ' '
        + tr('spi_legal', 'Rates from 0-36% APR. Payment options through Shop Pay Installments are subject to an eligibility check and are provided by these lending partners: <a href="https://www.affirm.com/lenders" target="_blank" rel="noopener">affirm.com/lenders</a>. Options depend on your purchase amount, and a down payment may be required. State notices to consumers: <a href="https://www.affirm.com/licenses" target="_blank" rel="noopener">affirm.com/licenses</a>.') + '</p>';
    }
    function escHtml(s) { return String(s == null ? '' : s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;'); }

    function renderProductOptions(selectedProductId) {
      if (!productsList.length) {
        return `<option value="">${productsLoading ? '⏳ Loading products…' : 'No products found'}</option>`;
      }
      return productsList.map(p => `<option value="${p.id}" data-handle="${escAttr(p.handle)}" ${p.id === selectedProductId ? 'selected' : ''}>${escHtml(p.title)}</option>`).join('');
    }

    function renderVariantOptions(productId, selectedVariantId) {
      if (!productsList.length) {
        return `<option value="">${productsLoading ? '⏳ Loading…' : '—'}</option>`;
      }
      const product = productsList.find(p => p.id === productId) || productsList[0];
      if (!product) return '<option value="">—</option>';
      return product.variants.map(v => `<option value="${v.id}" ${v.id === selectedVariantId ? 'selected' : ''}>${escHtml(v.title)} — $${v.price}</option>`).join('');
    }

    // Day before a YYYY-MM-DD date (for the registration-deadline default)
    function dayBefore(dateStr) {
      if (!/^\d{4}-\d{2}-\d{2}/.test(dateStr || '')) return '';
      const [y, m, d] = dateStr.slice(0, 10).split('-').map(Number);
      const dt = new Date(y, m - 1, d); dt.setDate(dt.getDate() - 1);
      const p = n => String(n).padStart(2, '0');
      return `${dt.getFullYear()}-${p(dt.getMonth() + 1)}-${p(dt.getDate())}`;
    }

    // Map a variant title to a canonical delivery format the calendar understands
    function toFormatVal(title) {
      const t = String(title || '').toLowerCase();
      if (t.indexOf('person') >= 0 || t.indexOf('on-site') >= 0 || t.indexOf('on site') >= 0) return 'On-site';
      if (t.indexOf('learning') >= 0) return 'eLearning';
      return 'Zoom';
    }

    // Pick the Shopify variant (carries the price) that matches a delivery format
    function variantForFormat(product, fmt) {
      if (!product || !product.variants || !product.variants.length) return null;
      const vs = product.variants;
      const find = re => vs.find(v => re.test(v.title));
      if (fmt === 'On-site') return find(/person|on-site|on site/i) || find(/zoom/i) || vs[0];
      if (fmt === 'eLearning') return find(/learning/i) || vs[0];
      return find(/zoom/i) || vs[0];
    }

    // ===== Searchable, categorized product picker (bundles expand to show their contents) =====
    function classifyProduct(p) {
      const tags = (p.tags || []).map(t => String(t).toLowerCase());
      const type = String(p.type || '').toLowerCase();
      const title = String(p.title || '').toLowerCase();
      if (tags.indexOf('full-compliance') >= 0 || type.indexOf('full compliance') >= 0) return 'fc';
      if (tags.indexOf('elective-bundle') >= 0 || type.indexOf('elective bundle') >= 0 || type.indexOf('bundle') >= 0) return 'eb';
      if (type.indexOf('exam') >= 0 || title.indexOf('exam prep') >= 0 || title.indexOf('boot camp') >= 0) return 'exam';
      if (title.indexOf('mandatory') >= 0) return 'mandatory';
      return 'individual';
    }
    function productById(id) { return productsList.find(p => p.id === id); }
    function productByHandle(h) { return productsList.find(p => p.handle === h); }
    const pickerOpenFolders = {};

    function pickerItemRow(p, selId) {
      const fmts = (p.variants || []).map(v => v.title).filter(t => !/learning/i.test(t)).join(' · ');
      return `<button type="button" class="ncca-cal__pick-item${p.id === selId ? ' is-sel' : ''}" data-pick-id="${escAttr(p.id)}" data-pick-handle="${escAttr(p.handle)}">
        <span class="ncca-cal__pick-title">${escHtml(p.title)}</span>
        ${fmts ? `<span class="ncca-cal__pick-meta">– ${escHtml(fmts)}</span>` : ''}
      </button>`;
    }
    function pickerFolder(b, selId, q) {
      const inc = Array.isArray(b.includes) ? b.includes : [];
      const selfMatch = (b.title || '').toLowerCase().includes(q);
      const incMatch = inc.some(i => (i.title || '').toLowerCase().includes(q));
      if (q && !selfMatch && !incMatch) return '';
      const open = q ? (selfMatch || incMatch) : !!pickerOpenFolders[b.id];
      const hrs = inc.reduce((sum, i) => sum + (parseInt(i.hours, 10) || 0), 0);
      const hasMand = inc.some(i => i.type === 'mandatory');
      const sub = (hrs ? hrs + ' CE Hours · ' : '') + 'Mandatory ' + (hasMand ? 'Included' : 'Not Included');
      const rows = inc.map(i => {
        const ip = productByHandle(i.handle);
        const tag = i.type === 'mandatory' ? 'Mandatory' : 'Elective';
        const lbl = `<span class="ncca-cal__pick-sub-t">${escHtml(i.title)}${i.hours ? ' · ' + escHtml(String(i.hours)) + 'h' : ''}</span><span class="ncca-cal__pick-sub-tag">${tag}</span>`;
        return ip
          ? `<button type="button" class="ncca-cal__pick-sub${ip.id === selId ? ' is-sel' : ''}" data-pick-id="${escAttr(ip.id)}" data-pick-handle="${escAttr(ip.handle)}">${lbl}</button>`
          : `<span class="ncca-cal__pick-sub ncca-cal__pick-sub--ro" title="Not separately schedulable">${lbl}</span>`;
      }).join('');
      return `<div class="ncca-cal__pick-folder${open ? ' is-open' : ''}" data-folder-id="${escAttr(b.id)}">
        <div class="ncca-cal__pick-foldhead">
          <button type="button" class="ncca-cal__pick-foldtoggle" data-fold-toggle="${escAttr(b.id)}">
            <svg class="ncca-cal__pick-caret" viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2.6" stroke-linecap="round" stroke-linejoin="round"><path d="m9 18 6-6-6-6"/></svg>
            <span class="ncca-cal__pick-foldtitle">${escHtml(b.title)}<span class="ncca-cal__pick-foldsub">${sub}</span></span>
          </button>
          <button type="button" class="ncca-cal__pick-foldsel${b.id === selId ? ' is-sel' : ''}" data-pick-id="${escAttr(b.id)}" data-pick-handle="${escAttr(b.handle)}">Select</button>
        </div>
        <div class="ncca-cal__pick-foldbody">${rows || '<span class="ncca-cal__pick-sub ncca-cal__pick-sub--ro">No contents listed</span>'}</div>
      </div>`;
    }
    function renderPickerList(selId, query) {
      if (!productsList.length) return `<p class="ncca-cal__pick-empty">${productsLoading ? '⏳ Loading products…' : 'No products found'}</p>`;
      const q = String(query || '').trim().toLowerCase();
      const g = { fc: [], eb: [], exam: [], individual: [], mandatory: [] };
      productsList.forEach(p => { (g[classifyProduct(p)] || g.individual).push(p); });
      const flat = arr => arr.filter(p => !q || (p.title || '').toLowerCase().includes(q)).map(p => pickerItemRow(p, selId)).join('');
      const sec = (label, html) => html ? `<div class="ncca-cal__pick-group"><p class="ncca-cal__pick-grouphd">${label}</p>${html}</div>` : '';
      let out = '';
      out += sec('Full Compliance Bundles', g.fc.map(b => pickerFolder(b, selId, q)).join(''));
      out += sec('Elective Bundles', g.eb.map(b => pickerFolder(b, selId, q)).join(''));
      out += sec('Individual Classes', flat([...g.mandatory, ...g.individual]));
      out += sec('Exam Prep', flat(g.exam));
      return out || `<p class="ncca-cal__pick-empty">No matches</p>`;
    }
    function applyPick(form, id, handle) {
      const hidden = form.querySelector('[data-picker-value]');
      if (hidden) hidden.value = id;
      const prod = productById(id);
      const handleInput = form.querySelector('input[name="productHandle"]');
      if (handleInput) handleInput.value = handle || (prod ? prod.handle : '');
      const label = form.querySelector('[data-picker-label]');
      if (label) label.textContent = prod ? prod.title : 'Select a class or bundle…';
      // Delivery format drives the variant (price). Keep the current format; recompute the variant + price for the new product.
      const fmtSel = form.querySelector('[data-format-input]');
      const fmt = fmtSel ? fmtSel.value : 'Zoom';
      const v = variantForFormat(prod, fmt);
      const variantHidden = form.querySelector('[data-variant-value]');
      if (variantHidden) variantHidden.value = v ? v.id : '';
      const priceEl = form.querySelector('[data-format-price]');
      if (priceEl) priceEl.textContent = v ? '$' + v.price : '—';
      const panel = form.querySelector('[data-picker-panel]');
      if (panel) panel.hidden = true;
    }

    function renderForm({ mode, session, defaultDate }) {
      const s = session || {};
      const productId = s.product || (s.productHandle ? productsList.find(p => p.handle === s.productHandle)?.id : null) || (productsList[0] && productsList[0].id) || '';
      const variantId = s.variant || s.variantGid || (productsList[0] && productsList[0].variants[0] && productsList[0].variants[0].id) || '';
      const date = s.date || defaultDate || '';
      const isEdit = mode === 'edit';
      const selProd = productsList.find(p => p.id === productId);
      const selectedTitle = selProd ? selProd.title : '';
      const selVariant = selProd ? (selProd.variants.find(v => v.id === variantId) || selProd.variants[0]) : null;
      const fmtVal = toFormatVal(s.format || (selVariant ? selVariant.title : ''));
      const fmtVariant = variantForFormat(selProd, fmtVal);
      const fmtVariantId = fmtVariant ? fmtVariant.id : (variantId || (selProd && selProd.variants[0] ? selProd.variants[0].id : ''));
      const fmtPrice = fmtVariant ? fmtVariant.price : '';
      const deadlineDefault = s.regDeadline ? String(s.regDeadline).slice(0, 10) : dayBefore(date);
      return `
        <form class="ncca-cal__edit-form" data-cal-edit-form>
          ${isEdit ? `<input type="hidden" name="id" value="${escAttr(s.id)}">` : ''}
          <input type="hidden" name="productHandle" value="${escAttr(s.productHandle || (productsList.find(p => p.id === productId)?.handle || ''))}">

          <div class="ncca-cal__field-grid">
            <label class="ncca-cal__field ncca-cal__field--full">
              <span>Product${isEdit ? ' <em style="font-style:italic;font-weight:normal;text-transform:none;letter-spacing:0;color:var(--ncca-gold);">(swapping product re-routes this date to the new product)</em>' : ''}</span>
              <input type="hidden" name="product" value="${escAttr(productId)}" data-picker-value>
              <div class="ncca-cal__picker" data-product-picker>
                <button type="button" class="ncca-cal__picker-trigger" data-picker-trigger>
                  <span data-picker-label>${escHtml(selectedTitle || 'Select a class or bundle…')}</span>
                  <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="m6 9 6 6 6-6"/></svg>
                </button>
                <div class="ncca-cal__picker-panel" data-picker-panel hidden>
                  <input type="text" class="ncca-cal__picker-search" data-picker-search placeholder="Search classes &amp; bundles…" autocomplete="off">
                  <div class="ncca-cal__picker-list" data-picker-list>${renderPickerList(productId, '')}</div>
                </div>
              </div>
            </label>

            <input type="hidden" name="variant" value="${escAttr(fmtVariantId)}" data-variant-value>

            <label class="ncca-cal__field">
              <span>Delivery format <em style="font-style:italic;font-weight:normal;text-transform:none;letter-spacing:0;color:var(--ncca-gold);">(price <span data-format-price>${fmtPrice ? '$' + fmtPrice : '—'}</span>)</em></span>
              <select name="format" data-format-input>
                <option value="Zoom"${fmtVal === 'Zoom' ? ' selected' : ''}>Zoom</option>
                <option value="On-site"${fmtVal === 'On-site' ? ' selected' : ''}>In-Person</option>
                <option value="eLearning"${fmtVal === 'eLearning' ? ' selected' : ''}>eLearning</option>
              </select>
            </label>

            <label class="ncca-cal__field">
              <span>City (in-person only)</span>
              <input type="text" name="city" value="${escAttr(s.city || '')}" placeholder="e.g. Raleigh">
            </label>

            <label class="ncca-cal__field">
              <span>Date</span>
              <input type="date" name="date" value="${escAttr(date)}" required>
            </label>

            <label class="ncca-cal__field">
              <span>Registration deadline <em style="font-style:italic;font-weight:normal;text-transform:none;letter-spacing:0;color:var(--ncca-text-muted);">(auto: day before · 4 PM ET)</em></span>
              <input type="date" name="registration_deadline" value="${escAttr(deadlineDefault)}">
            </label>

            <label class="ncca-cal__field ncca-cal__field--full">
              <span>Venue name</span>
              <input type="text" name="venue_name" value="${escAttr(s.venueName || '')}" placeholder="e.g. Hampton Inn Charlotte">
            </label>

            <label class="ncca-cal__field ncca-cal__field--full">
              <span>Venue address</span>
              <textarea name="venue_address" rows="2" placeholder="Street address, City, State ZIP">${escHtml(s.venueAddress || '')}</textarea>
            </label>

            <label class="ncca-cal__field">
              <span>Venue room</span>
              <input type="text" name="venue_room" value="${escAttr(s.venueRoom || '')}" placeholder="e.g. Conference Room B">
            </label>

            <label class="ncca-cal__field">
              <span>Instructor</span>
              <input type="text" name="instructor" value="${escAttr(s.instructor || '')}" placeholder="Malcolm Lee Boney">
            </label>

            <label class="ncca-cal__field">
              <span>Start time</span>
              <input type="text" name="start_time" value="${escAttr(s.start || s.start_time || '')}" placeholder="8:00 AM">
            </label>

            <label class="ncca-cal__field">
              <span>End time</span>
              <input type="text" name="end_time" value="${escAttr(s.end || s.end_time || '')}" placeholder="4:00 PM">
            </label>

            <label class="ncca-cal__field">
              <span>Seats total</span>
              <input type="number" name="seats_total" value="${escAttr(s.seatsTotal != null ? s.seatsTotal : '')}" min="0" placeholder="25">
            </label>

            <label class="ncca-cal__field">
              <span>Seats remaining</span>
              <input type="number" name="seats_remaining" value="${escAttr(s.seatsRemaining != null ? s.seatsRemaining : '')}" min="0" placeholder="25">
            </label>

            <label class="ncca-cal__field ncca-cal__field--full">
              <span>Internal notes</span>
              <textarea name="notes" rows="2" placeholder="Optional — only visible in admin">${escHtml(s.notes || '')}</textarea>
            </label>
          </div>

          <div class="ncca-cal__form-actions">
            <button type="button" class="ncca-cal__form-cancel">Cancel</button>
            <button type="submit" class="ncca-btn ncca-btn--gold">${isEdit ? '💾 Save Changes' : '+ Create Session'}</button>
          </div>
          <div class="ncca-cal__form-status" data-form-status></div>
        </form>
      `;
    }

    function renderCreateForm(iso) {
      currentFormState = { mode: 'create', defaultDate: iso };
      return `<div class="ncca-cal__edit-wrap">
                <p class="ncca-cal__edit-intro">Create a new class session. All required fields are marked.</p>
                ${renderForm({ mode: 'create', defaultDate: iso })}
              </div>`;
    }

    function renderEditForm(session) {
      currentFormState = { mode: 'edit', session };
      return `<div class="ncca-cal__edit-wrap">
                <p class="ncca-cal__edit-intro">Editing: <strong>${escHtml(session.productTitle)}</strong> on ${session.date}</p>
                ${renderForm({ mode: 'edit', session })}
              </div>`;
    }

    function wireFormEvents() {
      // Edit button → swap card with edit form
      drawerBody.querySelectorAll('[data-edit-session]').forEach(btn => {
        btn.addEventListener('click', () => {
          try {
            const s = JSON.parse(btn.dataset.editSession);
            drawerBody.innerHTML = renderEditForm(s);
            wireFormEvents();
          } catch (e) { console.error(e); }
        });
      });
      // "+ Add Another Event" on a day that already has sessions → swap to the create form
      drawerBody.querySelectorAll('[data-add-session]').forEach(btn => {
        btn.addEventListener('click', () => {
          const iso = btn.dataset.addSession;
          drawerTitle.textContent = `Add new session · ${fmtDisplayDate(iso)}`;
          drawerBody.innerHTML = renderCreateForm(iso);
          wireFormEvents();
        });
      });
      // Cancel resets form state
      drawerBody.querySelectorAll('.ncca-cal__form-cancel').forEach(btn => {
        btn.addEventListener('click', () => { currentFormState = null; });
      });

      // Delete button — show inline gold confirmation, no native confirm()
      drawerBody.querySelectorAll('[data-delete-session]').forEach(btn => {
        btn.addEventListener('click', () => {
          const id = btn.dataset.deleteSession;
          const title = btn.dataset.deleteTitle || 'this session';
          const sessionCard = btn.closest('.ncca-cal__session');
          if (!sessionCard) return;
          // Don't show twice
          if (sessionCard.querySelector('.ncca-cal__delete-confirm')) return;

          const confirmEl = document.createElement('div');
          confirmEl.className = 'ncca-cal__delete-confirm';
          confirmEl.innerHTML = `
            <p class="ncca-cal__delete-confirm-msg">⚠️ Delete <strong>${title}</strong>? This cannot be undone.</p>
            <div class="ncca-cal__delete-confirm-actions">
              <button type="button" class="ncca-cal__delete-confirm-cancel">Cancel</button>
              <button type="button" class="ncca-cal__delete-confirm-yes">🗑 Yes, delete</button>
            </div>
          `;
          // Insert below the admin controls (where Edit/Delete buttons live)
          const controls = sessionCard.querySelector('.ncca-cal__admin-controls');
          (controls || sessionCard).insertAdjacentElement('afterend', confirmEl);

          confirmEl.querySelector('.ncca-cal__delete-confirm-cancel').addEventListener('click', () => {
            confirmEl.remove();
          });
          confirmEl.querySelector('.ncca-cal__delete-confirm-yes').addEventListener('click', async (ev) => {
            const yesBtn = ev.currentTarget;
            yesBtn.disabled = true;
            yesBtn.textContent = 'Deleting…';
            try {
              const res = await fetch(workerUrl + '/sessions/delete', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'X-NCCA-Auth': workerSecret },
                body: JSON.stringify({ id }),
              });
              const data = await res.json();
              if (!res.ok) throw new Error(data.error || 'Delete failed');
              showSaveToast('🗑 Deleted!');
              setTimeout(() => location.reload(), 1100);
            } catch (e) {
              confirmEl.querySelector('.ncca-cal__delete-confirm-msg').innerHTML = `<strong style="color:#b21f1f">✗ ${e.message}</strong>`;
              yesBtn.disabled = false;
              yesBtn.textContent = '🗑 Yes, delete';
            }
          });
        });
      });

      // Cancel button → close drawer
      drawerBody.querySelectorAll('.ncca-cal__form-cancel').forEach(btn => {
        btn.addEventListener('click', () => { currentFormState = null; closeDrawer(); });
      });

      // Searchable product picker (categorized; bundles expand to show contents)
      drawerBody.querySelectorAll('[data-product-picker]').forEach(picker => {
        const form = picker.closest('form');
        const panel = picker.querySelector('[data-picker-panel]');
        const trigger = picker.querySelector('[data-picker-trigger]');
        const search = picker.querySelector('[data-picker-search]');
        if (trigger) trigger.addEventListener('click', () => {
          panel.hidden = !panel.hidden;
          // Focus the search box only on desktop (fine pointer) — on mobile, tapping it should raise the keyboard, not opening the dropdown
          if (!panel.hidden && search && window.matchMedia && window.matchMedia('(pointer: fine)').matches) {
            setTimeout(() => search.focus(), 30);
          }
        });
        if (search) search.addEventListener('input', () => {
          const cur = (form.querySelector('[data-picker-value]') || {}).value || '';
          panel.querySelector('[data-picker-list]').innerHTML = renderPickerList(cur, search.value);
        });
        if (panel) panel.addEventListener('click', e => {
          const fold = e.target.closest('[data-fold-toggle]');
          if (fold) {
            const fid = fold.getAttribute('data-fold-toggle');
            pickerOpenFolders[fid] = !pickerOpenFolders[fid];
            const fe = [...panel.querySelectorAll('.ncca-cal__pick-folder')].find(el => el.dataset.folderId === fid);
            if (fe) fe.classList.toggle('is-open', pickerOpenFolders[fid]);
            return;
          }
          const pick = e.target.closest('[data-pick-id]');
          if (pick) applyPick(form, pick.getAttribute('data-pick-id'), pick.getAttribute('data-pick-handle'));
        });
        document.addEventListener('click', e => { if (panel && !picker.contains(e.target)) panel.hidden = true; });
      });

      // Delivery format → drives the variant (and therefore the price) for the selected product
      drawerBody.querySelectorAll('select[name="format"]').forEach(sel => {
        sel.addEventListener('change', () => {
          const form = sel.closest('form');
          const prod = productById((form.querySelector('[data-picker-value]') || {}).value);
          const v = variantForFormat(prod, sel.value);
          const variantHidden = form.querySelector('[data-variant-value]');
          if (variantHidden) variantHidden.value = v ? v.id : '';
          const priceEl = form.querySelector('[data-format-price]');
          if (priceEl) priceEl.textContent = v ? '$' + v.price : '—';
        });
      });

      // Date change → registration deadline auto-recomputes to the day before
      drawerBody.querySelectorAll('input[name="date"]').forEach(dateInput => {
        dateInput.addEventListener('change', () => {
          const dl = dateInput.closest('form').querySelector('input[name="registration_deadline"]');
          if (dl) dl.value = dayBefore(dateInput.value);
        });
      });

      // Form submit → POST to worker
      const form = drawerBody.querySelector('[data-cal-edit-form]');
      if (form) {
        const isEditMode = !!form.querySelector('input[name="id"]');
        form.addEventListener('submit', async (e) => {
          e.preventDefault();
          const status = form.querySelector('[data-form-status]');
          const submitBtn = form.querySelector('button[type="submit"]');
          submitBtn.disabled = true;
          submitBtn.textContent = 'Saving…';
          status.textContent = '';
          status.className = 'ncca-cal__form-status';

          try {
            const fd = new FormData(form);
            const body = Object.fromEntries(fd.entries());
            // Apply default fallbacks for fields left blank (shown as light placeholders)
            if (!String(body.instructor || '').trim()) body.instructor = 'Malcolm Lee Boney';
            if (!String(body.start_time || '').trim()) body.start_time = '8:00 AM';
            if (!String(body.end_time || '').trim()) body.end_time = '4:00 PM';
            // Convert numeric fields (blank → default 25; an explicit 0 is preserved)
            const _st = parseInt(body.seats_total, 10); body.seats_total = isNaN(_st) ? 25 : _st;
            const _sr = parseInt(body.seats_remaining, 10); body.seats_remaining = isNaN(_sr) ? body.seats_total : _sr;

            const res = await fetch(workerUrl + '/sessions/upsert', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', 'X-NCCA-Auth': workerSecret },
              body: JSON.stringify(body),
            });
            const data = await res.json();
            console.log('NCCA save response:', { status: res.status, body: data, sent: body });
            if (!res.ok) {
              const detail = data.detail || (data.details ? JSON.stringify(data.details) : '');
              throw new Error((data.error || 'Save failed') + (detail ? ' — ' + detail : ''));
            }

            // Show centered success toast briefly, then reload
            showSaveToast('✓ Saved!');
            setTimeout(() => location.reload(), 1100);
          } catch (e) {
            status.textContent = '✗ ' + e.message;
            status.classList.add('ncca-cal__form-status--err');
            submitBtn.disabled = false;
            submitBtn.textContent = isEditMode ? '💾 Save Changes' : '+ Create Session';
          }
        });
      }
    }

    // Modal behavior — wraps the drawer in fixed-position overlay with backdrop
    let backdrop = null;
    function ensureBackdrop() {
      if (backdrop) return backdrop;
      backdrop = document.createElement('div');
      backdrop.className = 'ncca-cal__backdrop';
      backdrop.addEventListener('click', closeDrawer);
      document.body.appendChild(backdrop);
      return backdrop;
    }
    function openModal() {
      ensureBackdrop();
      backdrop.classList.add('ncca-cal__backdrop--show');
      drawer.classList.add('ncca-cal__drawer--modal');
      drawer.hidden = false;
      document.body.style.overflow = 'hidden';
      // Focus close button for keyboard users
      setTimeout(() => drawerClose && drawerClose.focus(), 0);
    }
    function closeDrawer() {
      drawer.hidden = true;
      drawer.classList.remove('ncca-cal__drawer--modal');
      if (backdrop) backdrop.classList.remove('ncca-cal__backdrop--show');
      document.body.style.overflow = '';
    }

    // Events
    daysContainer.addEventListener('click', e => {
      const btn = e.target.closest('[data-cal-open]');
      if (btn) openDay(btn.dataset.calOpen);
    });

    // Featured in-person cards → jump the grid to that date + open its modal.
    if (featuredTrack) {
      featuredTrack.addEventListener('click', e => {
        const card = e.target.closest('[data-featured-open]');
        if (!card) return;
        const iso = card.dataset.featuredOpen;
        const d = parseDate(iso);
        if (d) { userNavigated = true; view = new Date(d.getFullYear(), d.getMonth(), 1); render(); }
        if (calSection) calSection.scrollIntoView({ block: 'start' });
        openDay(iso);
      });
    }

    // Month cards → open that day's registration modal (card is already in the
    // month on screen, so no month change needed — just open the day).
    if (monthCardsEl) {
      monthCardsEl.addEventListener('click', e => {
        const card = e.target.closest('[data-mc-open]');
        if (!card) return;
        openDay(card.dataset.mcOpen);
      });
    }

    // External "Reserve" buttons (hero card, Shop Pay banner, sticky bar) open the
    // registration modal directly — no scroll. Falls through to the #calendar anchor
    // if there's no bookable session.
    document.addEventListener('click', e => {
      const btn = e.target.closest('[data-ncca-register]');
      if (!btn) return;
      const t = getRegisterTarget();
      if (!t) return;
      e.preventDefault();
      const d = parseDate(t.date);
      if (d) { userNavigated = true; view = new Date(d.getFullYear(), d.getMonth(), 1); render(); }
      openDay(t.date);
    });

    // Register form spinner — show loading state when add-to-cart submits
    drawerBody.addEventListener('submit', e => {
      const form = e.target.closest('[data-register-form]');
      if (!form) return;
      const btn = form.querySelector('button[type="submit"]');
      if (btn) {
        btn.classList.add('ncca-cal__btn--loading');
        btn.disabled = true;
      }
    });
    drawerClose.addEventListener('click', closeDrawer);
    document.addEventListener('keydown', e => { if (e.key === 'Escape' && !drawer.hidden) closeDrawer(); });
    // Changing month re-renders the month cards ABOVE the grid, and their height changes with the number of classes,
    // which used to push the calendar up or down the screen under the visitor's finger. Keep the clicked button at the
    // same spot on screen: measure it, re-render, then scroll by however far it moved (15 Oct 2026).
    // The browser's own scroll anchoring is switched off for the moment, so it can't correct a second time on top of
    // ours, and the correction jumps instantly even when the theme scrolls smoothly. A second pass on the next frame
    // catches anything that lays out late.
    function stayPut(btn, step) {
      const root = document.documentElement;
      const anchor = root.style.overflowAnchor;
      root.style.overflowAnchor = 'none';
      const before = btn.getBoundingClientRect().top;
      const settle = () => {
        const moved = btn.getBoundingClientRect().top - before;
        if (Math.abs(moved) > 1) window.scrollBy({ top: moved, left: 0, behavior: 'instant' });
      };
      step();
      settle();
      requestAnimationFrame(() => { settle(); requestAnimationFrame(() => { settle(); root.style.overflowAnchor = anchor; }); });
    }
    prevBtn.addEventListener('click', () => stayPut(prevBtn, () => { userNavigated = true; view = new Date(view.getFullYear(), view.getMonth() - 1, 1); render(); closeDrawer(); }));
    nextBtn.addEventListener('click', () => stayPut(nextBtn, () => { userNavigated = true; view = new Date(view.getFullYear(), view.getMonth() + 1, 1); render(); closeDrawer(); }));
    chips.forEach(chip => {
      chip.addEventListener('click', () => {
        chips.forEach(c => c.classList.remove('ncca-cal__chip--active'));
        chip.classList.add('ncca-cal__chip--active');
        currentFilter = chip.dataset.filterFormat;
        render();
        closeDrawer();
      });
    });

    render();
    renderFeatured();
    updateRegisterWidgets();
  }

  function boot() {
    document.querySelectorAll('[data-ncca-calendar]').forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
